import type { GoalConfig } from "../../../shared/goals/config";
import { GOAL_DEFAULTS, patienceFor } from "../../../shared/goals/defaults";
import { metaSettings } from "../../../shared/goals/meta";
import type { ProductProfile } from "../../../shared/goals/profile";
import type { GoalForm, GoalValues } from "./GoalStep";
import { dollarsTyped, isoDay } from "./text";

/** The goal step's form, filled from a goal's config: setting it up, or Edit goal. */
export function formOf(c: GoalConfig): GoalForm {
  return {
    sentence: c.sentence,
    budget: dollarsTyped(c.money.budgetCents),
    max: dollarsTyped(c.money.perResultMaxCents),
    page: metaSettings(c.channels.meta?.settings).link ?? "",
  };
}

/**
 * The page the site's buttons send people to most, from the profile: the one action it pushes. Same-page
 * anchors and the home page are left out; only buttons the profile opened and saw work count.
 */
export function pageFromProfile(p: ProductProfile): string | null {
  const home = p.website.replace(/\/+$/, "");
  const counts = new Map<string, number>();
  for (const c of p.calls_to_action) {
    const to = c.lands_on.trim().split(/\s/)[0];
    if (c.checked !== "works" || !to.startsWith("https://") || to.includes("#") || to.replace(/\/+$/, "") === home) continue;
    counts.set(to, (counts.get(to) ?? 0) + 1);
  }
  return [...counts].toSorted((a, b) => b[1] - a[1])[0]?.[0] ?? null;
}

/** The goal step's values as a merge patch of the goal's config. */
export function goalPatch(v: GoalValues, config: GoalConfig): Record<string, unknown> {
  const stopWasDefault = config.rules.stopNoResultCents === config.money.perResultMaxCents * GOAL_DEFAULTS.stopNoResultMultiple;
  const was = patienceFor(config.money.budgetCents);
  const patienceWasDefault = config.rules.patience.days === was.days && config.rules.patience.clicks === was.clicks;
  const patch: Record<string, unknown> = {
    sentence: v.sentence,
    result: { label: v.reading.what, target: v.reading.target, by: isoDay(v.reading.by) },
    money: { budgetCents: v.budgetCents, perResultMaxCents: v.perResultMaxCents },
  };
  // Why: the stop amount and the minimum data start from the money; they follow it until the owner sets them.
  const rules: Record<string, unknown> = {};
  if (stopWasDefault) rules.stopNoResultCents = v.perResultMaxCents * GOAL_DEFAULTS.stopNoResultMultiple;
  if (patienceWasDefault) rules.patience = patienceFor(v.budgetCents);
  if (Object.keys(rules).length) patch.rules = rules;
  // Why: the page lives where the Ads step reads it, the Meta channel's link; empty removes it.
  if (v.page !== undefined && config.channels.meta) patch.channels = { meta: { settings: { link: v.page } } };
  return patch;
}
